import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import classNames from 'classnames';
import { useI18n, LocalizedHtml } from '@udemy-v2/i18n';
import { Icon } from '@udemy-v2/icons';
import { SubscriptionPlanSubscriberState } from '@udemy-v2/subscription-graphql';
import { BlockList } from '@udemy-v2/react-core-components';
import { GwgSubscriptionCTA, GwgCheckoutProvider, SubscribeFromPageCookie, SubscriptionCTA } from '@udemy-v2/react-subscription-browse';
import { useGwgPlanPreference } from '@/features/course-landing-page/hooks/use-gwg-plan-preference';
import { CourseContentPlacement } from '@/features/course-landing-page/course-landing-page.react-component';
import styles from './grow-with-google-enrollment-box.module.scss';
import { useClpSubscriptionContext } from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import { useClpCourse } from '../../context/clp-course-provider.react-component';
import { useClpPurchaseContext } from '../../context/clp-purchase-context.react-component';
interface GrowWithGoogleEnrollmentBoxProps {
  placement: CourseContentPlacement;
}

/**
 * GrowWithGoogleCTA component renders the subscription CTA for Grow with Google courses.
 * It uses the GwgSubscriptionCTA component which handles plan precedence logic internally.
 */
const GWG_TIER_UPGRADE_URL = '/subscription-checkout/upgrade/?upgrade_type=tier&product_type=CONSUMERSUBSCRIPTION_PLUS_GROW_WITH_GOOGLE';
function GrowWithGoogleCTA() {
  const $ = _c(27);
  const {
    gettext
  } = useI18n();
  const course = useClpCourse();
  const {
    subscribedConsumerSubscriptionPlan,
    isValuePlanSubscriber
  } = useClpSubscriptionContext();
  const {
    gwgPlan,
    gwgPlusPersonalPlan,
    subscriptionTiersEnabled,
    isFeaturesLoading,
    isMonthlyPersonalPlanAvailable
  } = useClpPurchaseContext();
  const gwgPlanPreference = useGwgPlanPreference();
  const plan = gwgPlan?.isLoading && gwgPlusPersonalPlan?.isLoading === false ? undefined : gwgPlan;
  let t0;
  if ($[0] !== course.id) {
    t0 = () => {
      SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
    };
    $[0] = course.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleCtaClick = t0;
  if (subscriptionTiersEnabled && isValuePlanSubscriber) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        cta_1_url: GWG_TIER_UPGRADE_URL
      };
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const t2 = parseInt(course.id, 10);
    let t3;
    if ($[3] !== gettext) {
      t3 = gettext("Upgrade your plan");
      $[3] = gettext;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== gwgPlusPersonalPlan || $[6] !== handleCtaClick || $[7] !== t2 || $[8] !== t3) {
      t4 = <div className={styles["cta-container"]}><SubscriptionCTA plan={gwgPlusPersonalPlan} noticeData={t1} courseId={t2} uiRegion="purchase_section" offerCtaStyle="primary" className={styles["get-started-button"]} purchaseCtaText={t3} directCheckout={true} onCTAClick={handleCtaClick} /></div>;
      $[5] = gwgPlusPersonalPlan;
      $[6] = handleCtaClick;
      $[7] = t2;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t1;
  if ($[10] !== subscribedConsumerSubscriptionPlan) {
    t1 = subscribedConsumerSubscriptionPlan && subscribedConsumerSubscriptionPlan.subscribedPlan.productType ? {
      isSubscriber: true,
      isLoading: false,
      productType: subscribedConsumerSubscriptionPlan.subscribedPlan.productType
    } as SubscriptionPlanSubscriberState : undefined;
    $[10] = subscribedConsumerSubscriptionPlan;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const t2 = parseInt(course.id, 10);
  let t3;
  if ($[12] !== gettext || $[13] !== isFeaturesLoading || $[14] !== isMonthlyPersonalPlanAvailable) {
    t3 = isFeaturesLoading ? undefined : isMonthlyPersonalPlanAvailable ? gettext("Billed monthly or annually. Cancel anytime.") : gettext("Cancel anytime.");
    $[12] = gettext;
    $[13] = isFeaturesLoading;
    $[14] = isMonthlyPersonalPlanAvailable;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== course.trackingId || $[17] !== gwgPlusPersonalPlan || $[18] !== handleCtaClick || $[19] !== plan || $[20] !== t1 || $[21] !== t2 || $[22] !== t3) {
    t4 = <div className={styles["cta-container"]}><GwgSubscriptionCTA plan={plan} currentlySubscribedPlan={t1} combinedGwgPlan={gwgPlusPersonalPlan} courseId={t2} courseTrackingId={course.trackingId} uiRegion="purchase_section" offerCtaStyle="primary" className={styles["get-started-button"]} subtitleClassName={styles["billing-text"]} planPeriodText={t3} onCTAClick={handleCtaClick} /></div>;
    $[16] = course.trackingId;
    $[17] = gwgPlusPersonalPlan;
    $[18] = handleCtaClick;
    $[19] = plan;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== gwgPlanPreference || $[25] !== t4) {
    t5 = <GwgCheckoutProvider planPreference={gwgPlanPreference}>{t4}</GwgCheckoutProvider>;
    $[24] = gwgPlanPreference;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
}

/**
 * GrowWithGoogleEnrollmentBox component displays information about courses that are part of
 * the 'Learn AI with Google' subscription plan. Includes a notice, pricing, incentives, and enrollment CTA.
 */
export function GrowWithGoogleEnrollmentBox(t0) {
  const $ = _c(67);
  const {
    placement
  } = t0;
  const {
    gettext,
    interpolate
  } = useI18n();
  const isSidebarPlacement = CourseContentPlacement.SIDEBAR === placement;
  const {
    gwgPlan,
    gwgPlusPersonalPlan
  } = useClpPurchaseContext();
  const plan = gwgPlan || gwgPlusPersonalPlan;
  const formattedPrice = plan && "formattedMonthlyAmount" in plan ? plan.formattedMonthlyAmount : undefined;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classNames("ud-heading-xl", styles.title);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== gettext) {
    t2 = gettext("Purchase options");
    $[1] = gettext;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <h3 className={t1}>{t2}</h3>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = !isSidebarPlacement;
  let t5;
  if ($[5] !== t4) {
    t5 = classNames(styles.container, {
      [styles["container--with-margin"]]: t4
    });
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className={styles["icon-container"]}><Icon glyph="warning" size="medium" color="info" label={false} /></div>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = classNames("ud-heading-xs", styles["notice-title"]);
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== gettext) {
    t8 = gettext("Part of the 'Learn AI with Google' plan");
    $[9] = gettext;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <h3 className={t7}>{t8}</h3>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = classNames("ud-text-xs", styles["notice-description"]);
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== gettext) {
    t11 = gettext("This course can only be purchased as part of a subscription offering. ");
    $[14] = gettext;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = classNames("ud-text-xs", styles["learn-more-link"]);
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== gettext) {
    t13 = gettext("Learn more");
    $[17] = gettext;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t13) {
    t14 = <Link href="/partner/google/learn-ai/" className={t12} target="_self">{t13}</Link>;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t11 || $[22] !== t14) {
    t15 = <p className={t10}>{t11}{t14}</p>;
    $[21] = t11;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t15 || $[25] !== t9) {
    t16 = <div className={styles["notice-container"]}>{t6}<div className={styles["notice-content"]}>{t9}{t15}</div></div>;
    $[24] = t15;
    $[25] = t9;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== formattedPrice || $[28] !== gettext || $[29] !== interpolate) {
    t17 = formattedPrice && <div className={styles["price-container"]}><LocalizedHtml html={interpolate(gettext("<span class=\"label\">From</span> <span class=\"value\">%(price)s</span> <span class=\"period\">/month</span>"), {
        price: formattedPrice
      }, true)} interpolate={{
        label: <span className={classNames("ud-heading-sm", styles["price-label"])} />,
        value: <span className={classNames("ud-heading-xl", styles["price-value"])} />,
        period: <span className={classNames("ud-text-sm", styles["price-period"])} />
      }} /></div>;
    $[27] = formattedPrice;
    $[28] = gettext;
    $[29] = interpolate;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Icon glyph="sparkles" size="xsmall" color="neutral" label={false} aria-hidden="true" />;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== gettext) {
    t19 = gettext("3 career learning paths");
    $[32] = gettext;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19) {
    t20 = <BlockList.Item icon={t18}><span className="ud-text-sm">{t19}</span></BlockList.Item>;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Icon glyph="certificate" size="xsmall" color="neutral" label={false} aria-hidden="true" />;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== gettext) {
    t22 = gettext("20+ activities for real-world practice");
    $[37] = gettext;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t22) {
    t23 = <BlockList.Item icon={t21}><span className="ud-text-sm">{t22}</span></BlockList.Item>;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Icon glyph="verified" size="xsmall" color="neutral" label={false} aria-hidden="true" />;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== gettext) {
    t25 = gettext("Industry-recognized credentials from Google");
    $[42] = gettext;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== t25) {
    t26 = <BlockList.Item icon={t24}><span className="ud-text-sm">{t25}</span></BlockList.Item>;
    $[44] = t25;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Icon glyph="expand-plus" size="xsmall" color="neutral" label={false} aria-hidden="true" />;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== gettext || $[48] !== interpolate) {
    t28 = interpolate(gettext("Access to Google AI Pro for 3 months at no cost (<a class=\"terms-link\">terms apply</a>)"), true);
    $[47] = gettext;
    $[48] = interpolate;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      "terms-link": <a href="/terms/learn-with-google/" target="_blank" rel="noopener noreferrer" />
    };
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== t28) {
    t30 = <BlockList.Item icon={t27}><span className="ud-text-sm"><LocalizedHtml html={t28} interpolate={t29} /></span></BlockList.Item>;
    $[51] = t28;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t20 || $[54] !== t23 || $[55] !== t26 || $[56] !== t30) {
    t31 = <div className={styles["incentives-container"]}><BlockList size="small" padding="tight">{t20}{t23}{t26}{t30}</BlockList></div>;
    $[53] = t20;
    $[54] = t23;
    $[55] = t26;
    $[56] = t30;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <GrowWithGoogleCTA />;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== t16 || $[60] !== t17 || $[61] !== t31 || $[62] !== t5) {
    t33 = <div className={t5} data-purpose="grow-with-google-enrollment-box">{t16}{t17}{t31}{t32}</div>;
    $[59] = t16;
    $[60] = t17;
    $[61] = t31;
    $[62] = t5;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== t3 || $[65] !== t33) {
    t34 = <div className={styles["grow-with-google-enrollment-box"]}>{t3}{t33}</div>;
    $[64] = t3;
    $[65] = t33;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  return t34;
}